/**
 * Guarded UUID v4, safe in a NON-SECURE context.
 *
 * `crypto.randomUUID` is secure-context-only. Over plain http it is simply
 * `undefined`, and calling it throws `crypto.randomUUID is not a function`.
 * `crypto.getRandomValues` is NOT gated that way, which is what makes the
 * fallback below worth having rather than reaching straight for Math.random.
 *
 * ## Why this matters here and not in most apps
 *
 * Production is https, so a user never hits it. But this app is tested on a
 * real phone pointed at the dev server over the LAN - `http://192.168.x.x:3000`,
 * which next.config's allowedDevOrigins exists to permit - and several of its
 * worst bugs (the iOS fixed-bar drift, the floating rest bar) do not reproduce
 * in any simulator. So the insecure-context path is the one the device testing
 * runs on, and a crash there costs a testing session, not a user.
 *
 * It has already bitten twice. write-queue-engine.ts grew a private copy of
 * this guard after a bare randomUUID() lost sets in exactly the offline context
 * the queue exists for; public/offline-log.html grew its own. offline-snapshot
 * and the undo toast did not, and crashed the moment a phone loaded them over
 * http. Hence one implementation, in lib, where the next caller will find it.
 *
 * ## The fallback emits a REAL v4
 *
 * Not an `id-<ts>-<rand>` string. These ids land in `uuid` PK columns
 * (sets.id, workout_exercises.id) on upsert and Postgres rejects a malformed
 * uuid, so a "close enough" id means the write fails server-side, silently,
 * after the UI has already said it saved.
 */
export function newId(): string {
  const c = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;

  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }

  // Secure-context-gated randomUUID is out, but getRandomValues is not, so the
  // bytes are still cryptographically strong in every browser that has WebCrypto
  // at all. Set the version (4) and variant (10xx) bits by hand.
  if (c && typeof c.getRandomValues === "function") {
    const bytes = c.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex: string[] = [];
    for (let i = 0; i < 16; i++) hex.push(bytes[i].toString(16).padStart(2, "0"));
    return (
      hex.slice(0, 4).join("") +
      "-" +
      hex.slice(4, 6).join("") +
      "-" +
      hex.slice(6, 8).join("") +
      "-" +
      hex.slice(8, 10).join("") +
      "-" +
      hex.slice(10, 16).join("")
    );
  }

  // No WebCrypto at all (very old WebView). Math.random is not
  // cryptographically strong, but these ids only need to be unique within one
  // device's pending queue, not unguessable.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
